import { Button, Text, tokens, useNotify } from '@hotjar/hotsauce';
import { type Site, useCurrentSite } from '@hotjar/site';
import { useTranslation } from '@hotjar/translations';
import HotjarLogo from '@hotjar/shared-assets/images/logo-hotjar.svg';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import { postPreviewImage, putAccountUpdate, useOwnedAccount } from '@hotjar/account';

const LogoFormContainer = styled.div`
  display: flex;
  gap: ${tokens.spacer.x3};
  align-items: start;
`;

const AccountLogoContainer = styled.div`
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x2};
`;

const AccountLogoImage = styled.img`
  max-width: 200px;
  max-height: 48px;
`;

const SaveButton = styled(Button)`
  margin-top: ${tokens.spacer.x2};
`;

const HiddenInput = styled.input`
  display: none !important;
`;

const getImageAsBase64 = (image: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64Data = reader.result as string;
      resolve(base64Data);
    };

    reader.readAsDataURL(image);
  });
};

export function Logo(): JSX.Element {
  const { t } = useTranslation('common');
  const { data: currentSite, isLoading } = useCurrentSite();
  const { data: ownedAccount } = useOwnedAccount();
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [savingLogo, setSavingLogo] = useState(false);
  const [logo, setLogo] = useState<string | undefined>();
  const fileRef = useRef<HTMLInputElement>(null);
  const { showToast } = useNotify();

  useEffect(() => {
    if (currentSite?.account?.pictureUrl) {
      setLogo(currentSite.account.pictureUrl);
    }
  }, [currentSite]);

  async function uploadImageForResizeAndPreview() {
    if (!ownedAccount) return;

    setFileUploadError(null);

    const fileInput = fileRef.current;

    if (fileInput?.files?.[0] === undefined || currentSite?.account?.id === undefined) return;

    const [file] = fileInput.files;
    const imageBase64 = await getImageAsBase64(file);

    postPreviewImage(ownedAccount.id, imageBase64).then((response) => {
      if (response.error) {
        setFileUploadError(response.error);
      } else {
        setLogo(response.image);
      }
    });
  }

  function showSaveError() {
    setFileUploadError(t('settings-account.account-logo.save-error'));
  }

  function saveAccountLogo() {
    if (!ownedAccount?.id || !currentSite) return;

    setSavingLogo(true);

    setFileUploadError(null);

    putAccountUpdate(ownedAccount.id, ownedAccount.type, logo)
      .then((response) => {
        if (response.success) {
          showToast({
            text: t('settings-account.account-logo.save-success'),
            kind: 'success',
          });

          // Update the query cache for the current user with the new logo
          queryClient.setQueryData(USER_RESOURCES_QUERY_KEY, (oldData) => {
            // The data we get back here is not the currentSite, but actually the
            // result of the `/resources` endpoint so it contains organizations and
            // sites. We copy the data and then update the pictureUrl so that the
            // logo in the nav updates without needing to invalidate the query cache.
            const newData = structuredClone(oldData);

            newData.sites = newData.sites.map((site: Site) => {
              if (site.account.id === ownedAccount.id) {
                return {
                  ...site,
                  account: {
                    ...site.account,
                    pictureUrl: response.picture_url,
                  },
                };
              }

              return site;
            });

            return newData;
          });
        } else {
          showSaveError();
        }
      })
      .catch(() => {
        showSaveError();
      })
      .finally(() => {
        setSavingLogo(false);
      });
  }

  // Setting logo to undefined will show the hotjar logo instead.
  function resetAccountLogo() {
    setLogo(undefined);
  }

  return (
    <div>
      <Text as="h5" textType="header5">
        {t('settings-account.account-logo.title')}
      </Text>
      <Text as="p" textType="body2">
        {t('settings-account.account-logo.description')}
      </Text>

      <LogoFormContainer>
        <AccountLogoContainer>
          {!isLoading && <AccountLogoImage src={logo ?? HotjarLogo} alt="" />}
        </AccountLogoContainer>

        <Button onClick={() => fileRef?.current?.click()} kind="secondary" size="s">
          {t('settings-account.account-logo.choose-file-button')}
        </Button>
        <HiddenInput
          id="account-logo"
          ref={fileRef}
          type="file"
          data-testid="account-logo"
          aria-hidden
          onChange={uploadImageForResizeAndPreview}
        />

        <Button kind="secondary" size="s" onClick={resetAccountLogo}>
          {t('settings-account.account-logo.reset-button')}
        </Button>
      </LogoFormContainer>
      {fileUploadError && (
        <Text as="p" textType="body2" textColor="text_danger">
          {fileUploadError}
        </Text>
      )}

      <SaveButton
        isLoading={savingLogo}
        loadingText={t('settings-account.account-logo.save-button-loading')}
        isDisabled={currentSite?.account.pictureUrl === logo}
        onClick={saveAccountLogo}
      >
        {t('settings-account.account-logo.save-button')}
      </SaveButton>
    </div>
  );
}
